import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import { supabaseHelper } from '../lib/supabase';

interface MarketingToolProps {
  title: string;
  description: string;
  icon: JSX.Element;
  color: string;
  comingSoon?: boolean;
  onClick?: () => void;
}

const MarketingToolCard: React.FC<MarketingToolProps> = ({ 
  title, 
  description, 
  icon, 
  color,
  comingSoon = false, 
  onClick 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`relative transform transition-all duration-300 ${comingSoon ? 'opacity-70' : 'hover:-translate-y-2 cursor-pointer'}`}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card with glass morphism */}
      <div className="
        relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
        border border-white/30 shadow-lg group transition-all duration-300
        hover:shadow-xl p-6 h-full
      ">
        {/* Gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
        
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
          bg-gradient-to-br ${color} shadow-lg group-hover:scale-110 transition-transform
        `}>
          <span className="text-xl">{icon}</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
          {title}
          {comingSoon && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Coming Soon
            </span>
          )}
        </h3>
        <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
          {description}
        </p>
        
        {/* Hidden action button that appears on hover */}
        {!comingSoon && (
          <div className="
            absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
            transition-opacity transform group-hover:translate-x-0 translate-x-4
          ">
            <button className="
              flex items-center justify-center w-8 h-8 rounded-full 
              bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
            ">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Marketing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  
  const [businessPlans, setBusinessPlans] = useState<any[]>([]);
  
  // Fetch business plans for the user
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch business plans
        // This would be replaced with actual business plan fetching
        // For now we'll just use an empty array
        setBusinessPlans([]);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user]);
  


  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-16 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">
            Marketing Tools
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Powerful tools to help you market your business effectively</p>
        </div>
        

      
        {/* Tools Grid with 3D Cards and Glass Morphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <MarketingToolCard
            title="Brand Kit Creator"
            description="Create your complete brand identity in one place - business name, AI logo, and professional email signature. Everything flows together seamlessly."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>}
            color="from-orange-400 via-pink-500 to-purple-500"
            onClick={() => navigate('/marketing/brand-kit-creator')}
          />
          
          <MarketingToolCard
            title="LinkedIn Hub"
            description="Unified workspace for optimizing personal and business LinkedIn profiles. AI-generated headlines, summaries, and professional content in one place."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>}
            color="from-blue-500 via-indigo-600 to-blue-700"
            onClick={() => navigate('/linkedin-hub')}
          />
          
          {/* Social Media Post Generator removed - redundant (use Social Media Planner instead) */}
          
          <MarketingToolCard
            title="Social Media Planner"
            description="Plan, schedule, and publish AI-generated posts across platforms. Includes analytics, campaigns, and LinkedIn OAuth."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
            </svg>}
            color="from-blue-400 to-blue-600"
            onClick={() => navigate('/marketing/social-media-planner')}
          />

          <MarketingToolCard
            title="CRM"
            description="Manage contacts, companies, deals and activities in a multi-tenant workspace."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 009.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>}
            color="from-sky-400 to-blue-600"
            onClick={() => navigate('/crm')}
          />
          
          <MarketingToolCard
            title="Text to Image Generator"
            description="Create professional images for your social media and marketing materials using AI-powered image generation."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>}
            color="from-pink-400 to-rose-500"
            onClick={() => navigate('/marketing/text-to-image-generator')}
          />
          
          <MarketingToolCard
            title="Press Release Generator"
            description="Generate professional AP-style press releases to announce company news, product launches, and other milestones."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>}
            color="from-amber-400 to-orange-500"
            onClick={() => navigate('/marketing/press-release-generator')}
          />
          
          <MarketingToolCard
            title="SEO Content Hub"
            description="Complete SEO workflow: Generate topical authority maps, create optimized blog posts with keyword control, and manage your content library all in one place."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>}
            color="from-green-400 to-teal-500"
            onClick={() => navigate('/marketing/seo-content-hub')}
          />
          
          <MarketingToolCard
            title="SEO Tools Hub"
            description="Comprehensive SEO analysis and monitoring: Audit websites for performance, track Google/Bing rankings, monitor keywords, and optimize search visibility."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>}
            color="from-green-500 to-emerald-600"
            onClick={() => navigate('/marketing/seo-tools-hub')}
          />
          
          <MarketingToolCard
            title="Ecommerce Hub"
            description="AI-powered product content generation: Create compelling product descriptions, titles, and SEO-optimized copy that converts visitors into customers."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>}
            color="from-orange-500 to-red-600"
            onClick={() => navigate('/marketing/ecommerce-hub')}
          />
          

          
          <MarketingToolCard
            title="Business Launchpad"
            description="Plan and track your business launch with task management, calendar integration, and milestone tracking."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            </svg>}
            color="from-cyan-400 to-sky-500"
            onClick={() => navigate('/launchpad')}
          />
          
          <MarketingToolCard
            title="Print-on-Demand Designer"
            description="Create custom designs for t-shirts, posters, tote bags and more with AI-powered image generation."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 8l-4-4H8L4 8l2 4v8h12v-8l2-4z" />
            </svg>}
            color="from-violet-400 to-purple-500"
            onClick={() => navigate('/marketing/print-on-demand')}
          />
          
          <MarketingToolCard
            title="Text to Video Generator"
            description="Transform your ideas into stunning videos with AI. Create compelling video content for marketing and social media."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>}
            color="from-cyan-400 to-blue-500"
            onClick={() => navigate('/marketing/text-to-video-generator')}
          />
          
          <MarketingToolCard
            title="Cold Email Tool"
            description="Send personalized cold email campaigns with advanced tracking, SMTP integration, and automated follow-ups to grow your business."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>}
            color="from-red-400 to-pink-500"
            onClick={() => navigate('/email')}
          />

          <MarketingToolCard
            title="AI Telemarketing"
            description="Automated AI-powered cold calling campaigns. Real-time call monitoring, sentiment analysis, lead scoring, and automatic CRM sync. 97% cheaper than human SDRs."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>}
            color="from-purple-500 via-indigo-600 to-blue-600"
            onClick={() => navigate('/crm/telemarketing')}
          />

          <MarketingToolCard
            title="Email Signature Creator"
            description="Design a professional email signature with your avatar/logo, brand colors, and social links. Generate clean, sanitized HTML ready for Gmail/Outlook."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12H8m8-4H8m8 8H8M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
            </svg>}
            color="from-emerald-400 to-teal-500"
            onClick={() => navigate('/tools/email-signature')}
          />
          
          <MarketingToolCard
            title="Affiliate Management"
            description="Set up and manage your own affiliate program. Track referrals, customize signup forms, and process commissions."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 009.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>}
            color="from-emerald-400 to-green-500"
            onClick={() => navigate('/marketing/affiliate-management')}
          />

          <MarketingToolCard
            title="Link Tools Hub (Beta)"
            description="Unified workspace for URL shortening, QR code generation, and link analytics. Track clicks, manage links, and create trackable QR codes all in one place."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-2 2a4 4 0 11-5.656-5.656l1-1M10.172 13.828a4 4 0 010-5.656l2-2a4 4 0 115.656 5.656l-1 1" />
            </svg>}
            color="from-indigo-400 via-purple-500 to-pink-500"
            onClick={() => navigate('/link-tools-hub')}
          />

          <MarketingToolCard
            title="Notepad (Beta)"
            description="Paste text to generate a summary, study guide, and FAQ. Premium-only."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6M9 16h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm0 0V2m10 2V2" />
            </svg>}
            color="from-indigo-400 to-blue-500"
            onClick={() => navigate('/tools/notepad')}
          />

          <MarketingToolCard
            title="AI Support Desk"
            description="Enterprise support platform with AI responses, multi-channel inbox, SLA tracking, knowledge base, and unlimited agents. Manage customer support tickets efficiently."
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>}
            color="from-blue-500 via-cyan-500 to-teal-500"
            onClick={() => navigate('/admin/support')}
          />
          
          {/* Video Recorder and AI Website Generator removed - non-functional */}

        </div>

      </div>
    </section>
  );
};

export default Marketing;
